import React, { useState } from 'react';
import { Gap, Modal, Text } from '@common/components';
import { useLazyQuery, useMutation } from '@common/hooks';
import type { FullValidationErrorFragmentType } from '@common/graphql/fragments';
import { createMessage } from 'works/legacy/lib/message-helper';
import { datetime, todayIso } from '@common/services/datetime';
import type { LoadBalancingWorkerTermType } from '@load-balancing/graphql/queries/load-balancing-worker';
import { PROJECTED_UNASSIGNED_SHIFT_COUNT_QUERY } from '@load-balancing/graphql/queries/projected-unassigned-shift-count';
import { SAVE_WORKER_PROFILE_CHANGE_MUTATION } from '@load-balancing/graphql/mutations/save-worker-profile-change';
import {
  buildSaveVariables,
  initialFormValues,
  isEndBeforeStart,
  isPast,
  laterChanges,
  type WorkerProfileFormValues,
} from './_schedule-worker-profile-update-form-service';

// State + persistence for the "Update Profile" form (WORK-6258), shared by both hosts (the
// in-session profile panel and the Team Review drawer). The host fetches the worker's terms
// and renders the Save/Cancel chrome; this hook owns the form state, client-side date
// validation, and the mutation (refetching the read views on success).
export function useScheduleWorkerProfileUpdateForm({
  workerId,
  terms,
  asOf,
  editingTermId = null,
  schedulingPeriodId = null,
  workerName,
  onSaved,
  onUnassigningSave,
}: {
  workerId: string;
  terms: readonly LoadBalancingWorkerTermType[];
  asOf: string;
  editingTermId?: string | null;
  schedulingPeriodId?: string | null;
  // Names the worker in the deactivation confirmation (WORK-8875).
  workerName?: string;
  onSaved: () => void;
  // Fires when a confirmed deactivating save is about to persist — i.e. the backend will
  // sweep the worker's assigned shifts in [effectiveDate, endDate || ∞]. Lets the host
  // re-anchor UI keyed on a shift the sweep is removing (WORK-8874: the Shift Detail panel
  // follows its shift onto the reopened open slot instead of closing).
  onUnassigningSave?: (effectiveDate: string, endDate: string) => void;
}) {
  const [values, setValues] = useState<WorkerProfileFormValues>(() =>
    initialFormValues({ terms, asOf, editingTermId })
  );
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<
    FullValidationErrorFragmentType[]
  >([]);

  // Server-side count of the shifts a deactivating save would sweep, fetched fresh at
  // save time (#7416 review: a client-derived count only saw the schedule on screen,
  // while the sweep covers every in-review schedule — it could understate or skip the
  // warning entirely). network-only: the count must reflect the grid as of THIS save.
  const [fetchProjectedCount, { loading: countLoading }] = useLazyQuery(
    PROJECTED_UNASSIGNED_SHIFT_COUNT_QUERY,
    { fetchPolicy: 'network-only' }
  );

  const [mutate, { loading: saving }] = useMutation(
    SAVE_WORKER_PROFILE_CHANGE_MUTATION,
    {
      // Refresh the read views so the profile/Upcoming Changes reflect the change.
      refetchQueries: ['LoadBalancingWorker', 'ScheduleReview'],
      // errorPolicy is 'all', so GraphQL errors resolve here (not reject) with no worker.
      // Show inline validation errors, a toast for transport/GraphQL failures, and success
      // only when a worker actually came back.
      onCompleted: (data) => {
        const result = data?.saveWorkerProfileChange;
        const errors = result?.errors ?? [];
        if (errors.length > 0) {
          setServerErrors(errors);
          return;
        }
        if (!result?.worker) {
          createMessage(
            'error',
            'Could not save the profile change. Please try again.'
          );
          return;
        }
        createMessage('success', 'Profile change saved.');
        onSaved();
      },
      onError: () =>
        createMessage(
          'error',
          'Could not save the profile change. Please try again.'
        ),
    }
  );

  function update(patch: Partial<WorkerProfileFormValues>) {
    setValues((prev) => ({ ...prev, ...patch }));
  }

  function persist() {
    mutate({
      variables: buildSaveVariables({
        values,
        workerId,
        editingTermId,
        schedulingPeriodId,
      }),
    });
  }

  function save() {
    setSaveAttempted(true);
    // Effective Date is required and cannot be in the past.
    if (!values.effectiveDate || isPast(values.effectiveDate)) return;
    // End Date, when set, cannot fall before the effective date.
    if (isEndBeforeStart(values.effectiveDate, values.endDate)) return;

    // An ongoing (no end date) change supersedes everything after it; warn before removing
    // any later scheduled changes (only when creating). Confirming still routes through the
    // deactivation gate below, so both warnings fire when both apply.
    const replaced =
      !editingTermId && !values.endDate
        ? laterChanges(terms, values.effectiveDate)
        : [];
    if (replaced.length > 0) {
      confirmReplaceFutureChanges(
        values.effectiveDate,
        persistOrConfirmUnassign
      );
      return;
    }

    persistOrConfirmUnassign();
  }

  // Second confirmation gate (WORK-8875): the backend re-sweeps the SAVED window on
  // EVERY save of an inactive change, so whenever the form persists an inactive state
  // the modal decision keys on the live server count alone — 0 means the save removes
  // nothing (silent), anything else warns. That covers new changes, active→inactive
  // flips, widened windows, runs reopened since the first save, and rows re-captured
  // into the window, with no client-side guess about which happened (#7500 review;
  // replaces the earlier widened-window-only gate, whose "already swept" assumption
  // broke when a covering run was completed at first save and reopened later).
  async function persistOrConfirmUnassign() {
    if (values.accountActive) {
      persist();
      return;
    }

    const affectedCount = await projectedUnassignedCount();
    // A deactivation that touches no assignment needs no warning. null = the count is
    // unavailable (query failed) — fail safe: still warn, without the count line.
    if (affectedCount === 0) {
      persist();
      return;
    }

    confirmUnassignShifts({
      workerName,
      effectiveDate: values.effectiveDate,
      endDate: values.endDate,
      affectedCount,
      onConfirm: () => {
        onUnassigningSave?.(values.effectiveDate, values.endDate);
        persist();
      },
    });
  }

  // The sweep's own read path (projectedUnassignedShiftCount), so the warning counts
  // exactly what the save will remove — across every in-review schedule, not just the
  // one on screen.
  async function projectedUnassignedCount(): Promise<number | null> {
    // Read the error off THIS call's result, not the hook's render-level `error` — that
    // state is a stale closure inside the save flow. A rejected promise (invariant/link
    // errors) and a resolved-with-error result both mean "count unavailable" → null, and
    // the caller fails safe: warn without the count line.
    const result = await fetchProjectedCount({
      variables: {
        id: workerId,
        effectiveStartDate: values.effectiveDate,
        effectiveEndDate: values.endDate || null,
      },
    }).catch(() => null);
    if (!result || result.error) return null;

    return (
      result.data?.loadBalancingWorker?.projectedUnassignedShiftCount ?? null
    );
  }

  // `saving` also covers the projected-count fetch so the Save button spins from the
  // click, not only once the mutation starts.
  return {
    values,
    update,
    saveAttempted,
    serverErrors,
    saving: saving || countLoading,
    save,
  };
}

// Body of the deactivation confirm (WORK-8875): the scenario copy (immediate vs
// future-dated, with the end date when the inactive window is bounded), plus the
// affected-count line (null hides it — the count query failed and the modal fails safe
// to warning without a number).
function ConfirmUnassignShiftContent({
  immediate,
  name,
  effectiveDate,
  endDate,
  affectedCount,
}: {
  immediate: boolean;
  name: string;
  effectiveDate: string;
  endDate: string;
  affectedCount: number | null;
}) {
  const startLabel = immediate
    ? 'today'
    : datetime(effectiveDate).format('MMM D, YYYY');
  const windowLabel = endDate
    ? `${startLabel} through ${datetime(endDate).format('MMM D, YYYY')}`
    : startLabel;
  return (
    <Gap vertical size="sm" align="stretch">
      <Text>
        {immediate && !endDate
          ? `${name} will be inactive starting today. All assigned shifts during this schedule period will be unassigned.`
          : `${name} will be inactive starting ${windowLabel}. Any shifts assigned during the inactive period will be unassigned.`}
      </Text>
      {affectedCount !== null && (
        <Text>
          {`Affected shifts: ${affectedCount} assigned ${
            affectedCount === 1 ? 'shift' : 'shifts'
          }`}
        </Text>
      )}
    </Gap>
  );
}

// Warns that a deactivating save will auto-unassign the worker's assigned shifts covered
// by the inactive range (WORK-8875); runs the supplied save on confirm. Copy from the
// WORK-8875 acceptance criteria, refined per QA (end date shown for bounded windows).
function confirmUnassignShifts({
  workerName,
  effectiveDate,
  endDate,
  affectedCount,
  onConfirm,
}: {
  workerName?: string;
  effectiveDate: string;
  endDate: string;
  affectedCount: number | null;
  onConfirm: () => void;
}) {
  // The effective date can never be in the past, so on-or-before-today means the worker
  // becomes inactive immediately (Today–Ongoing) rather than at a future date.
  const immediate = effectiveDate <= todayIso();
  const name = workerName ?? 'This worker';

  Modal.confirm({
    title: immediate
      ? 'All assigned shifts will be unassigned'
      : 'Assigned shifts will be unassigned',
    content: (
      <ConfirmUnassignShiftContent
        immediate={immediate}
        name={name}
        effectiveDate={effectiveDate}
        endDate={endDate}
        affectedCount={affectedCount}
      />
    ),
    okText: 'Save and Unassign',
    cancelText: 'Cancel',
    danger: true,
    onOk: onConfirm,
  });
}

// Warns that an ongoing change with no end date will remove the worker's later scheduled
// changes; runs the supplied save on confirm. Copy approved by design (Vicky Pan).
function confirmReplaceFutureChanges(
  effectiveDate: string,
  onConfirm: () => void
) {
  Modal.confirm({
    title: 'Replace future scheduled changes?',
    content: (
      <Gap vertical size="sm" align="stretch">
        <Text>
          {`This change has no end date and will become the worker's profile from ${datetime(
            effectiveDate
          ).format(
            'MMM D, YYYY'
          )} onward. Any scheduled changes after that date will be removed.`}
        </Text>
        <Text>
          To keep future scheduled changes, add an end date to make this a
          temporary change.
        </Text>
      </Gap>
    ),
    okText: 'Confirm',
    onOk: onConfirm,
  });
}
